import type { MessageKey } from "@erp/i18n";
import { IMPORT_TARGETS, type ImportDuplicateMode, type ImportReport, type ImportTarget } from "@erp/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { CheckCircle2, FileUp, RotateCcw } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Badge, type BadgeTone } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Panel, Segmented } from "~/components/ui/page";
import { Select } from "~/components/ui/select";
import {
  autoMap,
  buildRow,
  decodeText,
  fieldOfPath,
  IMPORT_FIELDS,
  missingRequired,
  parseCsv,
  type FieldKey,
  type Mapping,
  type Sheet,
} from "~/features/settings/import-model";
import { api, unwrap } from "~/lib/api";
import { cn } from "~/lib/cn";
import { errorMessageKey } from "~/lib/errors";
import { parseXlsx } from "~/features/settings/xlsx";
import { translateFieldError, useT } from "~/lib/i18n";
import { HelpTip } from "~/components/ui/help-tip";
import { useCan } from "~/lib/session";
import { ShopifySyncButton } from "~/features/settings/shopify-sync";
import { IabakoSyncPanel } from "~/features/settings/iabako-sync";

export const Route = createFileRoute("/_app/settings/imports")({ component: ImportsPage });

/** Imports, and the Shopify sync that the guide gives to the same people (permission `imports:run`). */
function ImportsPage() {
  const t = useT();
  const can = useCan();
  const capabilities = useQuery({
    queryKey: ["integrations", "capabilities"],
    queryFn: () => unwrap(api.integrations.capabilities.get()),
  });
  return (
    <div className="flex flex-col gap-5">
      {capabilities.data?.shopify ? (
        <Panel className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between md:p-6">
          <div className="min-w-0">
            <h2 className="text-[15px] font-medium">{t("imports.shopifySync.title")}</h2>
            <p className="text-sm text-fg-2">{t("imports.shopifySync.body")}</p>
          </div>
          <ShopifySyncButton />
        </Panel>
      ) : null}
      {capabilities.data?.iabako && can("settings:manage") ? <IabakoSyncPanel /> : null}
      <ImportWizard />
    </div>
  );
}

const STEPS = ["file", "mapping", "preview", "report"] as const;
type Step = (typeof STEPS)[number];
const NONE = "__none";
/** Requests stay small enough for the API body limit and give steady progress. */
const CHUNK = 500;
const PREVIEW_ROWS = 50;

interface Loaded extends Sheet {
  name: string;
}

function send(
  target: ImportTarget,
  body: { duplicates: ImportDuplicateMode; dryRun: boolean; rows: { line: number; data: Record<string, unknown> }[] },
) {
  switch (target) {
    case "customers":
      return unwrap(api.imports.customers.post(body));
    case "suppliers":
      return unwrap(api.imports.suppliers.post(body));
    case "products":
      return unwrap(api.imports.products.post(body));
    case "documents":
      return unwrap(api.imports.documents.post(body));
  }
}

async function runChunks(
  target: ImportTarget,
  duplicates: ImportDuplicateMode,
  dryRun: boolean,
  rows: { line: number; data: Record<string, unknown> }[],
) {
  const total: ImportReport = { created: 0, updated: 0, skipped: 0, renamed: 0, compositions: 0, duplicateLines: [], errors: [] };
  for (let i = 0; i < rows.length; i += CHUNK) {
    const part = await send(target, { duplicates, dryRun, rows: rows.slice(i, i + CHUNK) });
    total.created += part.created;
    total.updated += part.updated;
    total.skipped += part.skipped;
    total.renamed += part.renamed;
    total.compositions += part.compositions;
    total.duplicateLines.push(...part.duplicateLines);
    total.errors.push(...part.errors);
  }
  return total;
}

function ImportWizard() {
  const t = useT();
  const can = useCan();
  const queryClient = useQueryClient();
  const [target, setTarget] = useState<ImportTarget>("customers");
  const [step, setStep] = useState<Step>("file");
  const [sheet, setSheet] = useState<Loaded | null>(null);
  const [mapping, setMapping] = useState<Mapping>([]);
  const [duplicates, setDuplicates] = useState<ImportDuplicateMode>("skip");
  const [preview, setPreview] = useState<ImportReport | null>(null);
  const [report, setReport] = useState<ImportReport | null>(null);

  const rows = () => (sheet ? sheet.rows.map((cells, i) => ({ line: i + 2, data: buildRow(target, mapping, cells) })) : []);

  const check = useMutation({
    mutationFn: () => runChunks(target, duplicates, true, rows()),
    onSuccess: (result) => {
      setPreview(result);
      setStep("preview");
    },
    onError: (error) => toast.error(t(errorMessageKey(error))),
  });
  const run = useMutation({
    mutationFn: () => runChunks(target, duplicates, false, rows()),
    onSuccess: (result) => {
      setReport(result);
      setStep("report");
      toast.success(t("imports.imported"));
      void queryClient.invalidateQueries({ queryKey: [target] });
    },
    onError: (error) => toast.error(t(errorMessageKey(error))),
  });

  const reset = () => {
    setStep("file");
    setSheet(null);
    setMapping([]);
    setPreview(null);
    setReport(null);
  };

  const loaded = (next: Loaded) => {
    setSheet(next);
    setMapping(autoMap(target, next.headers));
    setStep("mapping");
  };

  const changeTarget = (next: ImportTarget) => {
    setTarget(next);
    setPreview(null);
    if (sheet) setMapping(autoMap(next, sheet.headers));
    if (step === "preview" || step === "report") setStep(sheet ? "mapping" : "file");
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-0.5">
          <HelpTip id="settings.import" />
        </div>
        <Segmented
          label={t("imports.target")}
          value={target}
          onChange={changeTarget}
          // Document archives count in the revenue reports: their import is kept to administrators.
          options={IMPORT_TARGETS.filter((v) => v !== "documents" || can("settings:manage")).map((v) => ({
            value: v,
            label: t(`imports.targets.${v}`),
          }))}
        />
      </div>

      <ol className="flex gap-1 text-[13px]" aria-label={t("imports.title")}>
        {STEPS.map((s, i) => {
          const index = STEPS.indexOf(step);
          return (
            <li
              key={s}
              aria-current={s === step ? "step" : undefined}
              className={cn(
                "flex flex-1 items-center gap-2 border-t-2 pt-2",
                i < index
                  ? "border-ink text-fg-2 dark:border-gold"
                  : i === index
                    ? "border-gold font-medium text-fg"
                    : "border-line text-fg-3",
              )}
            >
              <span className="tabular">{i + 1}</span>
              <span className="max-sm:sr-only">{t(`imports.steps.${s}`)}</span>
            </li>
          );
        })}
      </ol>

      {step === "file" ? <FileStep onLoaded={loaded} /> : null}

      {step === "mapping" && sheet ? (
        <MappingStep
          target={target}
          sheet={sheet}
          mapping={mapping}
          onMappingChange={setMapping}
          duplicates={duplicates}
          onDuplicatesChange={setDuplicates}
          onBack={reset}
          onNext={() => check.mutate()}
          checking={check.isPending}
        />
      ) : null}

      {step === "preview" && sheet && preview ? (
        <PreviewStep
          sheet={sheet}
          mapping={mapping}
          report={preview}
          duplicates={duplicates}
          onBack={() => setStep("mapping")}
          onRun={() => run.mutate()}
          running={run.isPending}
        />
      ) : null}

      {step === "report" && report ? <ReportStep target={target} report={report} onRestart={reset} /> : null}
    </div>
  );
}

function FileStep({ onLoaded }: { onLoaded: (sheet: Loaded) => void }) {
  const t = useT();
  const [error, setError] = useState<MessageKey | null>(null);
  const [dragging, setDragging] = useState(false);

  const read = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    try {
      const buffer = await file.arrayBuffer();
      const sheet = /\.xlsx$/i.test(file.name) ? await parseXlsx(buffer) : parseCsv(decodeText(buffer));
      if (sheet.headers.length === 0 || sheet.rows.length === 0) return setError("imports.emptyFile");
      onLoaded({ ...sheet, name: file.name });
    } catch {
      setError("imports.fileError");
    }
  };

  return (
    <Panel className="p-5 md:p-6">
      <div
        className={cn(
          "relative flex min-h-52 flex-col items-center justify-center gap-3 rounded-panel border-2 border-dashed px-6 py-10 text-center transition-colors focus-within:outline-2 focus-within:outline-gold",
          dragging ? "border-gold bg-gold-soft" : "border-line-strong hover:bg-surface-2",
        )}
      >
        <FileUp className="size-7 text-fg-3" aria-hidden />
        <span className="font-medium">{t("imports.dropzone")}</span>
        <span className="max-w-md text-sm text-fg-3">{t("imports.csvOnly")}</span>
        {/* The native input covers the zone: it takes clicks and file drops itself. */}
        <input
          type="file"
          accept=".csv,.xlsx,text/csv,text/plain,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          aria-label={t("imports.dropzone")}
          className="absolute inset-0 cursor-pointer opacity-0"
          onDragEnter={() => setDragging(true)}
          onDragLeave={() => setDragging(false)}
          onDrop={() => setDragging(false)}
          onChange={(e) => {
            void read(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
      </div>
      {error ? (
        <p role="alert" className="mt-3 text-sm font-medium text-danger">
          {t(error)}
        </p>
      ) : null}
    </Panel>
  );
}

function MappingStep({
  target,
  sheet,
  mapping,
  onMappingChange,
  duplicates,
  onDuplicatesChange,
  onBack,
  onNext,
  checking,
}: {
  target: ImportTarget;
  sheet: Loaded;
  mapping: Mapping;
  onMappingChange: (mapping: Mapping) => void;
  duplicates: ImportDuplicateMode;
  onDuplicatesChange: (mode: ImportDuplicateMode) => void;
  onBack: () => void;
  onNext: () => void;
  checking: boolean;
}) {
  const t = useT();
  const fieldLabel = (key: FieldKey) => t(`imports.fields.${key}`);
  const missing = missingRequired(target, mapping, duplicates);
  const options = [
    { value: NONE, label: t("imports.ignore") },
    ...IMPORT_FIELDS[target].map((f) => ({ value: f.key as string, label: fieldLabel(f.key) })),
  ];
  const setColumn = (index: number, value: string) => {
    const key = value === NONE ? null : (value as FieldKey);
    // A field maps to one column only: taking it elsewhere frees the previous column.
    onMappingChange(mapping.map((m, i) => (i === index ? key : m === key ? null : m)));
  };
  const sample = (index: number) => sheet.rows.find((r) => r[index])?.[index] ?? "";

  return (
    <div className="flex flex-col gap-5">
      <Panel>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-4 md:px-6">
          <div className="min-w-0">
            <h2 className="text-[15px] font-medium">{t("imports.mapping")}</h2>
            <p className="text-sm text-fg-2">
              {t("imports.fileSummary", { name: sheet.name, count: sheet.rows.length, columns: sheet.headers.length })}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onBack}>
            {t("imports.changeFile")}
          </Button>
        </div>
        <div className="hidden grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.1fr)] gap-4 border-b border-line px-6 py-2 text-[12.5px] text-fg-3 md:grid">
          <span>{t("imports.column")}</span>
          <span>{t("imports.sample")}</span>
          <span>{t("imports.field")}</span>
        </div>
        <ul className="divide-y divide-line">
          {sheet.headers.map((header, index) => (
            <li
              key={`${header}-${index}`}
              className="grid gap-2 px-5 py-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.1fr)] md:items-center md:gap-4 md:px-6"
            >
              <span className="truncate font-medium">{header}</span>
              <span className="truncate text-sm text-fg-3">{sample(index)}</span>
              <Select
                aria-label={header}
                value={mapping[index] ?? NONE}
                onValueChange={(v) => setColumn(index, v)}
                options={options}
              />
            </li>
          ))}
        </ul>
      </Panel>

      <Panel className="flex flex-col gap-3 px-5 py-4 md:px-6">
        <h2 className="text-[15px] font-medium">{t("imports.duplicates")}</h2>
        <p className="text-sm text-fg-2">{t(`imports.duplicateHints.${target}`)}</p>
        <Segmented
          label={t("imports.duplicates")}
          value={duplicates}
          onChange={onDuplicatesChange}
          options={(["skip", "update"] as const).map((v) => ({ value: v, label: t(`imports.duplicateModes.${v}`) }))}
        />
        {target === "customers" ? <p className="text-xs text-fg-3">{t("imports.kindValues")}</p> : null}
      </Panel>

      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
        {missing.length ? (
          <p role="alert" className="text-sm text-danger sm:mr-auto">
            {t("imports.missingRequired", { fields: missing.map((group) => group.map(fieldLabel).join(" / ")).join(", ") })}
          </p>
        ) : null}
        <Button variant="primary" onClick={onNext} disabled={missing.length > 0} loading={checking}>
          {checking ? t("imports.checking") : t("imports.check")}
        </Button>
      </div>
    </div>
  );
}

function useErrorText() {
  const t = useT();
  return (fields: Record<string, string>) =>
    Object.entries(fields)
      .map(([path, message]) => {
        const field = fieldOfPath(path);
        const text = translateFieldError(t, message) ?? t("errors.validation");
        return field ? `${t(`imports.fields.${field}`)} — ${text}` : text;
      })
      .join(" · ");
}

function PreviewStep({
  sheet,
  mapping,
  report,
  duplicates,
  onBack,
  onRun,
  running,
}: {
  sheet: Loaded;
  mapping: Mapping;
  report: ImportReport;
  duplicates: ImportDuplicateMode;
  onBack: () => void;
  onRun: () => void;
  running: boolean;
}) {
  const t = useT();
  const errorText = useErrorText();
  const errors = new Map(report.errors.map((e) => [e.line, e.fields]));
  const dupes = new Set(report.duplicateLines);
  const shown = mapping
    .map((m, i) => (m ? i : -1))
    .filter((i) => i >= 0)
    .slice(0, 4);
  // Errors first: they are what the user needs to look at.
  const lines = sheet.rows
    .map((cells, i) => ({ line: i + 2, cells }))
    .toSorted((a, b) => Number(errors.has(b.line)) - Number(errors.has(a.line)));
  const importable = report.created + (duplicates === "update" ? report.updated : 0);

  const status = (line: number): { tone: BadgeTone; label: string } =>
    errors.has(line)
      ? { tone: "danger", label: t("imports.status.invalid") }
      : dupes.has(line)
        ? { tone: "warning", label: t("imports.status.duplicate") }
        : { tone: "success", label: t("imports.status.new") };

  return (
    <div className="flex flex-col gap-5">
      <Summary report={report} />
      <Panel>
        <div className="border-b border-line px-5 py-4 md:px-6">
          <h2 className="text-[15px] font-medium">{t("imports.preview")}</h2>
          {report.errors.length ? <p className="mt-0.5 text-sm text-fg-2">{t("imports.previewHint")}</p> : null}
        </div>
        <ul className="divide-y divide-line">
          {lines.slice(0, PREVIEW_ROWS).map(({ line, cells }) => {
            const s = status(line);
            const fieldErrors = errors.get(line);
            return (
              <li key={line} className="flex flex-col gap-1 px-5 py-3 md:px-6">
                <div className="flex items-start gap-3">
                  <span className="w-16 shrink-0 text-xs text-fg-3 tabular">{t("imports.line", { line })}</span>
                  <div className="grid min-w-0 flex-1 gap-x-4 gap-y-0.5 text-sm sm:grid-cols-2 lg:grid-cols-4">
                    {shown.map((i) => (
                      <span key={i} className="truncate">
                        {cells[i] || <span className="text-fg-3">{t("common.states.none")}</span>}
                      </span>
                    ))}
                  </div>
                  <Badge tone={s.tone} className="shrink-0">
                    {s.label}
                  </Badge>
                </div>
                {fieldErrors ? <p className="pl-19 text-xs text-danger">{errorText(fieldErrors)}</p> : null}
              </li>
            );
          })}
        </ul>
        {lines.length > PREVIEW_ROWS ? (
          <p className="border-t border-line px-5 py-3 text-sm text-fg-3 md:px-6">
            {t("imports.previewMore", { count: lines.length - PREVIEW_ROWS })}
          </p>
        ) : null}
      </Panel>
      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <Button variant="ghost" onClick={onBack}>
          {t("common.actions.back")}
        </Button>
        <Button variant="primary" onClick={onRun} loading={running} disabled={importable === 0}>
          {t("imports.run", { count: importable })}
        </Button>
      </div>
    </div>
  );
}

function Summary({ report }: { report: ImportReport }) {
  const t = useT();
  const items: { key: keyof typeof report & ("created" | "updated" | "skipped" | "renamed" | "compositions"); tone: string }[] = [
    { key: "created", tone: "text-success" },
    { key: "updated", tone: "text-info" },
    { key: "skipped", tone: "text-fg-2" },
    // Migration counters, shown only when the file uses those columns.
    ...(report.renamed ? [{ key: "renamed" as const, tone: "text-info" }] : []),
    ...(report.compositions ? [{ key: "compositions" as const, tone: "text-info" }] : []),
  ];
  return (
    <ul className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {items.map((item) => (
        <li key={item.key} className="rounded-panel border border-line bg-surface px-4 py-3 text-sm">
          <span className={cn("font-medium", item.tone)}>{t(`imports.summary.${item.key}`, { count: report[item.key] })}</span>
        </li>
      ))}
      <li className="rounded-panel border border-line bg-surface px-4 py-3 text-sm">
        <span className={cn("font-medium", report.errors.length ? "text-danger" : "text-fg-2")}>
          {t("imports.summary.errors", { count: report.errors.length })}
        </span>
      </li>
    </ul>
  );
}

function ReportStep({ target, report, onRestart }: { target: ImportTarget; report: ImportReport; onRestart: () => void }) {
  const t = useT();
  const errorText = useErrorText();
  return (
    <div className="flex flex-col gap-5">
      <Panel className="flex flex-col items-center gap-3 px-6 py-10 text-center">
        <CheckCircle2 className="size-8 text-success" aria-hidden />
        <h2 className="font-display text-lg font-medium">{t("imports.imported")}</h2>
        <p className="text-sm text-fg-2">{t(`imports.targets.${target}`)}</p>
      </Panel>
      <Summary report={report} />
      {report.errors.length ? (
        <Panel>
          <ul className="divide-y divide-line">
            {report.errors.map((e) => (
              <li key={e.line} className="flex gap-3 px-5 py-3 text-sm md:px-6">
                <span className="w-16 shrink-0 text-xs text-fg-3 tabular">{t("imports.line", { line: e.line })}</span>
                <span className="text-danger">{errorText(e.fields)}</span>
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}
      <Button onClick={onRestart} className="self-start">
        <RotateCcw />
        {t("imports.restart")}
      </Button>
    </div>
  );
}
